import { ModalRef, useTranslation } from '@msdk/common'
import { DefaultActionType } from '@msdk/definitions'
import {
  Box,
  ButtonAlternative,
  ButtonIcon,
  ButtonPrimary,
  ButtonSecondary,
  CloseIcon,
  COLORS,
  createComponentWrapperWithRef,
  DialogActions,
  DialogTitle,
  Divider as UiDivider,
  styled,
  Typography,
  uiTokens,
} from '@msdk/ui'
import React, { ReactNode, useEffect } from 'react'

/* Dialog title */
const BaseStyledDialogTitle = styled(DialogTitle)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
`

const NovaStyledDialogTitle = styled(BaseStyledDialogTitle)`
  font-weight: ${uiTokens.font.fontWeight.semiBold};
  padding: 0 16px;
`

const StyledDialogTitle = createComponentWrapperWithRef(
  {
    mui: BaseStyledDialogTitle,
    nova: NovaStyledDialogTitle,
  },
  'StyledDialogTitle',
)

/* Content */
const Content = styled(Box)`
  padding: 16px 24px;
  width: 444px;
  overflow-y: auto;
`

/* Button alternative */
const DeleteButton = styled(ButtonAlternative)`
  background-color: ${COLORS.error.main};
`

/* Divider */
const Divider = createComponentWrapperWithRef(
  {
    mui: UiDivider,
    nova: () => null,
  },
  'Divider',
)

/* Title */
const Title = createComponentWrapperWithRef(
  {
    mui: ({ children }: { children: ReactNode }) => (
      <Typography variant="h6" component="span">
        {children}
      </Typography>
    ),
    nova: ({ children }: { children: ReactNode }) => children,
  },
  'Title',
)

type Props = {
  modalRef?: ModalRef
  onConfirm: () => void
  title: string
  body: string
  confirmationLabel: string
  type: DefaultActionType
}

export function ActionConfirmationModal({ onConfirm, modalRef, title, body, confirmationLabel, type }: Props) {
  /**
   * Split the body string to uphold line breaks
   */
  const sBody = body.split(/{break}/)
  const t = useTranslation()

  function closeModal() {
    modalRef && modalRef.close()
  }

  function handleConfirm() {
    onConfirm()
    closeModal()
  }

  useEffect(() => {
    /**
     * Getting the parent element and resetting padding
     */
    const content = document.querySelector('.MuiDialogContent-root') as HTMLElement
    content.style.padding = '0'
  }, [])

  return (
    <>
      <StyledDialogTitle>
        <Title>{title}</Title>
        <ButtonIcon onClick={closeModal}>
          <CloseIcon />
        </ButtonIcon>
      </StyledDialogTitle>
      <Divider />
      <Content>
        {sBody.map((e, i) => (
          <React.Fragment key={`body-line-${i}`}>
            <Typography variant="body1">{e}</Typography>
            {i !== sBody.length - 1 && <br />}
          </React.Fragment>
        ))}
      </Content>
      <Divider />
      <DialogActions>
        <ButtonSecondary onClick={closeModal}>
          <Typography variant="button">{t('CMPSR_ACTION_CONFIRMATION_CANCEL', 'Cancel')}</Typography>
        </ButtonSecondary>
        {type === DefaultActionType.Delete && (
          <DeleteButton onClick={handleConfirm} color="error">
            <Typography variant="button">{confirmationLabel}</Typography>
          </DeleteButton>
        )}
        {type !== DefaultActionType.Delete && (
          <ButtonPrimary onClick={handleConfirm}>
            <Typography variant="button">{confirmationLabel}</Typography>
          </ButtonPrimary>
        )}
      </DialogActions>
    </>
  )
}
